'use client';
import React from 'react';
import Image from 'next/image';

interface Row {
  id: number;
  chartImage: string;
  chartAlt: string;
  chartFirst: boolean;
  eyebrow: string;
  heading: string;
  body: string;
  bullets?: string[];
}

const rows: Row[] = [
  {
    id: 1,
    chartImage: '/assets/images/image-1781010853956.png',
    chartAlt: 'U.S. Listed Companies bar chart showing the 40% decline in publicly traded companies since 2006',
    chartFirst: false,
    eyebrow: 'Shrinking Public Markets',
    heading: 'The Universe of U.S. Listed Companies Has Shrunk Dramatically',
    body: 'Alternative investments have become a significant growth area within the financial services industry for one very good reason – the number of publicly traded companies has declined by 40% since 2006 as companies are staying private longer. Thus, investors opportunity sets have been vastly diminished if they limit themselves solely to the public markets.',
  },
  {
    id: 2,
    chartImage: '/assets/images/image-1780957334155.png',
    chartAlt: 'U.S. public and private companies with $100M+ LTM revenue chart showing private companies generate the majority of U.S. revenue',
    chartFirst: true,
    eyebrow: 'Private vs. Public Revenue',
    heading: 'Private Companies Now Generate the Majority of U.S. Revenue',
    body: 'Private companies account for approximately 66% of U.S. business revenues, yet most retail and advisory portfolios have zero exposure to this segment. The economic engine of America is increasingly private — and portfolios should reflect that reality.',
    bullets: [
      '66% of U.S. business revenue is generated by private companies',
      'Public markets represent only a fraction of total economic activity',
      'Excluding private markets means missing the dominant share of growth',
    ],
  },
  {
    id: 3,
    chartImage: '/assets/images/Global_Alt_Assets-1780957437761.png',
    chartAlt: 'Global Alternative Assets Under Management chart showing growth over time',
    chartFirst: false,
    eyebrow: 'Global AUM Growth',
    heading: 'Global Alternative AUM Has Grown to over $20 Trillion',
    body: 'Institutional capital has been flowing into alternatives for decades. Global alternative assets under management have grown from roughly $4 trillion in 2008 to over $20 trillion today — a testament to the risk-adjusted return potential that sophisticated investors have long recognized.',
    bullets: [
      'AUM has more than tripled since 2008 across all alternative categories',
      'Private equity, private debt, real assets, and infrastructure all expanding',
      'Institutional adoption validates the long-term case for alternatives',
    ],
  },
  {
    id: 4,
    chartImage: '/assets/images/private_vs._public_market_returns-1780957474406.png',
    chartAlt: 'Hypothetical growth of $100,000 chart showing $908K vs $467K outcome comparing private market allocations to traditional 60/40 portfolio over two decades',
    chartFirst: true,
    eyebrow: 'Performance Advantage',
    heading: '$908K vs. $467K — The Cost of Ignoring Alternatives',
    body: 'A $100,000 investment in a portfolio with private market allocations would have grown to approximately $908,000 over the past two decades — nearly double the $467,000 outcome of a traditional 60/40 portfolio. The compounding advantage of alternatives is significant and measurable.',
    bullets: [
      'Nearly 2× the terminal wealth versus a traditional portfolio',
      'Outperformance driven by private equity, private credit, and real assets',
      'The gap widens over time as compounding amplifies the return differential',
    ],
  },
  {
    id: 5,
    chartImage: '/assets/images/60.40_portfolio_blackstone-1780957673900.png',
    chartAlt: 'Two donut charts comparing a traditional 60/40 portfolio versus a portfolio with alternative investments, showing improved return, yield, and reduced volatility metrics',
    chartFirst: false,
    eyebrow: 'Uncorrelated Returns',
    heading: 'Alternatives Improve Both Returns and Volatility',
    body: 'One very important added benefit of alternatives when compared with a traditional 60% equity and 40% fixed income portfolio, is that a private market portfolio can take advantage of uncorrelated returns which positively impact both portfolio returns and volatility. In this example, adding alternative investments has resulted in an almost 100 basis point improvement in return and yield metrics, while annualized volatility has declined substantially.',
  },
  {
    id: 6,
    chartImage: '/assets/images/image-1781013330766.png',
    chartAlt: 'Two donut charts comparing Traditional 60/40 portfolio allocation versus a portfolio With Private Market Allocations, accompanied by a performance table showing improved annualized return, lower volatility, and higher current yield',
    chartFirst: true,
    eyebrow: 'Efficient Frontier',
    heading: 'Alternatives Shift the Efficient Frontier in Your Favor',
    body: 'Modern portfolio theory demonstrates that adding low-correlation assets expands the efficient frontier — enabling portfolios to achieve higher expected returns at any given level of risk. Alternatives are the primary lever institutional investors use to move beyond the constraints of a traditional stock-and-bond portfolio.',
    bullets: [
      'Shifts the risk-return frontier upward and to the left',
      'Enables higher returns without proportionally increasing risk',
      'The theoretical foundation behind institutional portfolio construction',
    ],
  },
  {
    id: 7,
    chartImage: '/assets/images/correlation_public_equity_and_fixed_income-1780957808035.png',
    chartAlt: 'Annual Correlation between Public Equity and Fixed Income chart showing increasing correlations over time',
    chartFirst: false,
    eyebrow: 'Correlation Trends',
    heading: 'Rising Correlations Limit Traditional Portfolio Diversification',
    body: 'Interestingly, a big source of negative correlation between public equities and fixed income has been the occurrence of a multi-decade bond bull market which saw yields decline substantially. With this secular trend now in the rear-view mirror, correlations between public equity and fixed income have been increasing and thereby limiting the diversification benefits of a traditional 60/40 portfolio.',
  },
  {
    id: 8,
    chartImage: '/assets/images/alternative_investment_categories-1780957858795.png',
    chartAlt: 'Alternative Investment Categories pie chart showing diverse sectors including private equity, real estate, infrastructure, hedge funds, private credit, and natural resources',
    chartFirst: true,
    eyebrow: 'Alternative Investment Categories',
    heading: 'Alternatives Are as Diverse as the Private Economy',
    body: 'Private markets can also provide unique investment opportunities by allowing early participation in meaningful secular trends in specific economic sectors like the AI buildout, digital economy or life sciences/biotech. Alternative investments can also provide access to specific property types, infrastructure, or geographic regions that can further differentiate return/risk profiles. In short, alternative come in many different flavors and are as diverse as the private economy.',
  },
  {
    id: 9,
    chartImage: '/assets/images/image-1781014142996.png',
    chartAlt: 'Investment objectives comparison table showing private market strategies spanning from capital appreciation through income generation and tax avoidance',
    chartFirst: false,
    eyebrow: 'Investment Objectives',
    heading: 'Private Markets Cover the Full Spectrum of Financial Objectives',
    body: 'As with public markets, investors can pursue specific financial objectives using private markets that cover the spectrum from capital appreciation through income generation and tax avoidance. Bespoke opportunities within private markets can be tailored to individual\'s interests and/or values.',
  },
];

export default function WhyAlternatives() {
  return (
    <section id="why-alts" className="bg-white">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-20 pb-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-0.5" style={{ backgroundColor: '#c9a84c', height: '2px' }} />
          <span
            className="text-xs font-semibold tracking-widest uppercase"
            style={{ color: '#c9a84c', letterSpacing: '0.2em' }}
          >
            Why Alternatives
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2
              className="font-black mb-4"
              style={{
                color: '#1a2744',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1.15',
                fontFamily: 'Lato, sans-serif',
              }}
            >
              The Case for
              <span className="block" style={{ color: '#4060a1' }}>
                Alternative Investments
              </span>
            </h2>
          </div>
          <div>
            <p className="text-base leading-relaxed" style={{ color: '#666666' }}>
              Alternative investments have historically provided superior risk-adjusted returns, reduced
              portfolio volatility, and access to return streams uncorrelated with traditional markets.
              Institutional investors have long recognized this advantage — now independent advisors can too.
            </p>
          </div>
        </div>
      </div>

      {/* Alternating Rows */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-20 space-y-0">
        {rows.map((row, index) => {
          const isEven = index % 2 === 0;
          const bgColor = isEven ? '#ffffff' : '#f5f6f8';

          return (
            <div
              key={row.id}
              style={{ backgroundColor: bgColor }}
              className={index < rows.length - 1 ? "border-b border-[#e8eaf0]" : ""}
            >
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-16">
                <div className={`flex flex-col ${row.chartFirst ? "lg:flex-row-reverse" : "lg:flex-row"} gap-12 lg:gap-16 lg:items-center`}>
                  {/* Text block — always first in DOM = always first on mobile */}
                  <div className="w-full lg:w-1/2">
                    <div className="flex flex-col justify-center py-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-6 h-0.5 flex-shrink-0" style={{ backgroundColor: '#c9a84c' }} />
                        <span
                          className="text-xs font-semibold tracking-widest uppercase"
                          style={{ color: '#c9a84c', letterSpacing: '0.18em' }}
                        >
                          {row.eyebrow}
                        </span>
                      </div>
                      <h3
                        className="font-black mb-4 leading-tight"
                        style={{
                          color: '#1a2744',
                          fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                          fontFamily: 'Lato, sans-serif',
                        }}
                      >
                        {row.heading}
                      </h3>
                      <p className="text-sm leading-relaxed mb-5" style={{ color: '#555555' }}>
                        {row.body}
                      </p>
                      {row.bullets && (
                        <ul className="space-y-2">
                          {row.bullets.map((bullet, bi) => (
                            <li key={bi} className="flex items-start gap-3">
                              <span
                                className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: '#c9a84c' }}
                              />
                              <span className="text-sm leading-relaxed" style={{ color: '#444444' }}>
                                {bullet}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {/* Image block — always second in DOM = always second on mobile */}
                  <div className="w-full lg:w-1/2 flex items-center justify-center">
                    <div className="w-full relative" style={{ minHeight: '280px' }}>
                      <Image
                        src={row.chartImage}
                        alt={row.chartAlt}
                        width={600}
                        height={400}
                        className="w-full h-auto object-contain rounded"
                        style={{ maxHeight: '420px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* The Challenge Section */}
      <div style={{ backgroundColor: '#0f2240' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Column: Title + Paragraphs */}
            <div>
              {/* Eyebrow label */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-0.5" style={{ backgroundColor: '#c9a84c', height: '2px' }} />
                <span
                  style={{
                    color: '#c9a84c',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                  }}
                >
                  The Challenge
                </span>
              </div>
              {/* Main heading */}
              <h2
                className="font-black mb-8"
                style={{
                  color: '#ffffff',
                  fontSize: 'clamp(2rem, 4vw, 3.25rem)',
                  lineHeight: '1.1',
                  fontFamily: 'Lato, sans-serif',
                }}
              >
                The Challenge of{' '}
                <span style={{ color: '#c9a84c' }}>Investing in</span>
                <br />
                <span style={{ color: '#c9a84c' }}>Alternatives</span>
              </h2>
              <p className="text-base mb-6 leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
                Investing in alternatives is not without its challenges and requires access to a broad
                universe of opportunities, the insight to select the appropriate vehicles as well as
                appropriate levels of due diligence on both managers and the investment specifics. And,
                as with most investments, a good deal of patience can also be required.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
                Bottom line, without careful due diligence and management, investors may struggle to
                separate true sources of alpha from strategies that simply add cost or risk.
              </p>
            </div>

            {/* Right Column: Bullet Points */}
            <div className="flex flex-col justify-center gap-8">
              {[
                {
                  title: 'Complexity and opacity',
                  body: 'There is a wide dispersion of manager skill and limited transparency.',
                },
                {
                  title: 'Liquidity constraints',
                  body: 'Investors need to have longer investment horizons and expect reduced flexibility.',
                },
                {
                  title: 'Higher costs and fees',
                  body: 'Managers will charge more for specialized strategies and structures that may or may not be acceptable to the investor.',
                },
                {
                  title: 'Operational demands',
                  body: 'Private market investing requires greater due diligence, monitoring, and analysis of integration risk.',
                },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <span
                    className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full"
                    style={{ backgroundColor: '#c9a84c' }}
                  />
                  <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
                    <span className="font-semibold" style={{ color: '#ffffff' }}>
                      {item.title}:
                    </span>{' '}
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
